import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const WHATMORE_SCRIPT = 'https://d1qflh9ill7vje.cloudfront.net/whatmore.js';
const WHATMORE_SHOP_ID = 'STRF8S43FI9';

function removeWhatmoreScripts() {
    document
        .querySelectorAll(`script[src*="whatmore.js"], script[data-whatmore-loader="1"]`)
        .forEach((node) => node.parentNode && node.parentNode.removeChild(node));
}

function injectWhatmoreScript() {
    return new Promise((resolve) => {
        removeWhatmoreScripts();

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `${WHATMORE_SCRIPT}?v=${Date.now()}`;
        script.async = true;
        script.setAttribute('data-whatmore-loader', '1');
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

function ImageSlider({ showwatchshop, sectionTitle }) {
    const mountedRef = useRef(false);
    const [status, setStatus] = useState('idle'); // idle | loading | ready | empty

    useEffect(() => {
        if (!showwatchshop) return undefined;

        let cancelled = false;
        let pollTimer;
        let safetyTimer;

        const start = async () => {
            // Wait one frame so config + render-root are in the DOM
            await new Promise((r) => requestAnimationFrame(() => r()));
            if (cancelled) return;

            setStatus('loading');
            const ok = await injectWhatmoreScript();
            if (cancelled) return;

            if (!ok) {
                setStatus('empty');
                return;
            }

            // Poll for rendered video tiles (Whatmore paints asynchronously)
            let tries = 0;
            const poll = () => {
                if (cancelled) return;
                const root = document.querySelector('.watch-and-shop .whatmore-render-root');
                const hasTiles = !!(
                    root &&
                    (root.querySelector('video, img, iframe, canvas, [class*="whatmore"], [class*="carousel"]') ||
                        root.childElementCount > 0)
                );

                if (hasTiles) {
                    setStatus('ready');
                    return;
                }

                tries += 1;
                if (tries < 20) {
                    pollTimer = setTimeout(poll, 400);
                } else {
                    setStatus('empty');
                }
            };

            pollTimer = setTimeout(poll, 500);
            safetyTimer = setTimeout(() => {
                if (!cancelled) {
                    const root = document.querySelector('.watch-and-shop .whatmore-render-root');
                    setStatus(root && root.childElementCount > 0 ? 'ready' : 'empty');
                }
            }, 9000);
        };

        // Avoid double-inject in React Strict Mode remounts
        if (!mountedRef.current) {
            mountedRef.current = true;
            start();
        } else {
            start();
        }

        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
            if (safetyTimer) clearTimeout(safetyTimer);
        };
    }, [showwatchshop]);

    if (!showwatchshop) return null;

    return (
        <div className="watch-and-shop">
            <div className="App">
                <div className="BreakPointContainer">
                    <div className="CarouselSectionContainer">
                        <div className="layout row justify-space-between align-center text-left SectionTitle">
                            <h1 className="m-t-0 m-b-0 demi-bold w-auto">
                                {sectionTitle || 'WATCH AND SHOP'}
                            </h1>
                            <Link
                                to="/category"
                                className="ViewAllUrlsLink orat-color-hover h5 demi-bold w-auto"
                            >
                                VIEW ALL
                            </Link>
                        </div>

                        <div className={`watch-and-shop-shell status-${status}`}>
                            {(status === 'idle' || status === 'loading') && (
                                <div className="watch-and-shop-skeleton" aria-hidden="true">
                                    {[0, 1, 2, 3].map((i) => (
                                        <div className="watch-and-shop-skeleton-card" key={i} />
                                    ))}
                                </div>
                            )}

                            {status === 'empty' && (
                                <div className="watch-and-shop-empty">
                                    <p>Videos will appear here soon. Please check back later.</p>
                                </div>
                            )}

                            {/* Stable markup — must exist BEFORE whatmore.js runs */}
                            <div
                                className="whatmore-base watch-and-shop-widget"
                                style={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: status === 'empty' ? 0 : 300,
                                }}
                            >
                                <div id="whatmoreShopId" data-wh={WHATMORE_SHOP_ID} />
                                <div id="whatmoreHeading" data-wh="" />
                                <div id="whatmoreTitleFontSize" data-wh="22" />
                                <div id="whatmoreLandscapePadding" data-wh="12" />
                                <div id="whatmoreTopBottomPadding" data-wh="10" />
                                <div id="whatmoreVideoTileSize" data-wh="60" />
                                <div id="whatmoreVideoTileSizePortrait" data-wh="78" />
                                <div id="whatmoreTitleFont" data-wh="sans-serif" />
                                <div id="whatmorePrimaryFont" data-wh="sans-serif" />
                                <div id="whatmoreShowViews" data-wh="false" />
                                <div className="whatmore-template-type" data-wh="template-j" />
                                <div id="whatmoreIsInDesignMode" data-wh="false" />
                                <div className="whatmore-widget" data-wh="carousel" />
                                <div className="whatmore-render-root" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageSlider;
